import { Button, ButtonMode, ButtonSize, Checkbox, Divider, Icon, ICONS, Popup } from '@creditsights/styleguide-web';
import { difference, uniq } from 'lodash';
import React, { ReactElement, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useSetRecoilState } from 'recoil';
import { toasterState } from '../../atoms/toaster.atom';
import { USER_WATCHLISTS_KEY } from '../../Constants';
import useFetchUserWatchlists from '../../hooks/user/useFetchUserWatchlists';
import useSaveWatchlist from '../../hooks/user/useSaveWatchlist';
import './AddToWatchlist.scss';
import NewWatchlistModal from './NewWatchlistModal';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
function AddToWatchlist({ companies }): ReactElement {
  const queryClient = useQueryClient();
  const showToaster = useSetRecoilState(toasterState);
  const [open, setOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { data: watchlists, isLoading } = useFetchUserWatchlists();
  const { mutate: saveWatchlist, isLoading: saveProcessing, reset, isSuccess } = useSaveWatchlist();

  useEffect(() => {
    if (isSuccess) {
      reset();
      queryClient.invalidateQueries([USER_WATCHLISTS_KEY]);
    }
  }, [isSuccess]);

  const handleChanges = (checked, watchlist) => {
    let array = watchlist.tags;
    let isAdd = true;
    if (checked) {
      array = uniq(array.concat(companies));
    } else if (!checked) {
      isAdd = false;
      array = difference(watchlist.tags, companies);
    }

    saveWatchlist(
      {
        id: watchlist.id,
        watchlist: {
          ...watchlist,
          tags: array,
        },
      },
      {
        onSuccess: () => {
          showToaster({
            text: isAdd ? 'Companies were successfully added to your watchlist' : 'Companies were successfully removed from your watchlist',
            bold: true,
          });
        },
      }
    );
  };

  const renderCheckbox = (watchlist) => {
    const array = difference(companies, watchlist.tags);
    const indeterminate = array.length !== 0 && array.length < companies.length;
    const checked = array.length === 0;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return <Checkbox key={watchlist.id} className="watchlist" label={watchlist.name} indeterminate={indeterminate} checked={checked} dataTestId="watchlist-checkbox" onClick={(e, data) => handleChanges(!checked, watchlist)} />;
  };

  return (
    <div className="cs-add-to-watchlist-ctx">
      <Popup
        className="cs-add-to-watchlist-popup"
        basic
        on="click"
        position="bottom right"
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        trigger={
          <Button mode={ButtonMode.PRIMARY} size={ButtonSize.MEDIUM} loading={isLoading}>
            <Icon height={10} width={10} name={ICONS.Plus} /> Add to Watchlist
          </Button>
        }
        content={
          <div className="contents">
            {open && !isLoading && (
              <>
                <div className="current-watchlists">{watchlists && watchlists.map((item) => renderCheckbox(item))}</div>
                <Divider />
                <Button className="create-new-watchlist" mode={ButtonMode.TERTIARY} disabled={saveProcessing} onClick={() => setShowModal(true)}>
                  <Icon width={12} height={12} name={ICONS.Plus} /> Create New Watchlist
                </Button>
              </>
            )}
          </div>
        }
      />
      {watchlists && <NewWatchlistModal open={showModal} companies={companies} invalidNames={watchlists.map((wl) => wl.name)} onClose={() => setShowModal(false)} />}
    </div>
  );
}

export default AddToWatchlist;
